import {
  ASYNC_JOB_STATUS_WAITING,
  ASYNC_JOB_STATUS_RUNNING,
  ASYNC_JOB_STATUS_FAILED,
  ASYNC_JOB_STATUS_REJECTED,
} from '@picsio/db/src/constants';
import React, {
  useState, useEffect, useCallback, useRef, useMemo,
} from 'react';
import {
  arrayOf, func, number, any, string, bool, oneOf,
} from 'prop-types';
import { useLocation } from 'react-router-dom';
import { IconButton } from '@picsio/ui';
import {
  Copy as CopyIcon, CheckIcon, Idea,
} from '@picsio/icons';
import { useDispatch } from 'react-redux';
import cn from 'classnames';
import TranscriptSearch from '../TranscriptSearch';
import TranscriptMenu from '../TranscriptMenu';
import { downloadSubFile } from '../../../../helpers/transcript';
import * as utils from '../../utils';
import Logger from '../../../../services/Logger';
import { deleteTranscript } from '../../../../store/actions/assets';
import CopyTranscriptDialog from '../CopyTranscriptDialog';
import ViewsSelector from '../ViewsSelector';
import { LocalStorage } from '../../../../shared/utils';
import Tooltip from '../../../Tooltip';
import l10n from '../../../../shared/strings';
import sdk from '../../../../sdk';
import Toast from '../../../Toast';
import copyTextToClipboard from '../../../../helpers/copyTextToClipboard';
import handleSuggestImprovement from '../../../toolbars/handlers/handleSuggestImprovement';

import TranscriptPlaceholder from '../TranscriptPlaceholder';
import Transcript from '../Transcript';
import Keywords from '../Keywords';
import Ocr from '../Ocr';
import AiSummary from '../AiSummary';
import Actions from '../Actions';
import Chapters from '../Chapters';

import VIEWS_OPTIONS from '../ViewsSelector/options';
import PromoBlock from '../PromoBlock/PromoBlock';

const VIEWS = VIEWS_OPTIONS.map((option) => option.value);
const DEFAULT_VIEW = VIEWS[0];
const TRANSCRIPT_VIEW = VIEWS.find((value) => value === 'transcript');
export const LOCAL_STORAGE_VIEW_KEY = 'picsio.videoAiView';
export const CHANGE_VIEW_EVENT = 'preview:videoAiView:change';
const LOCAL_STORAGE_EXPLORE_KEY = 'picsio.videoAiExploreBlockHide';
const COPY_FEEDBACK_TIMEOUT_MS = 2000;
const PLACEHOLDER_STATUSES = [
  ASYNC_JOB_STATUS_RUNNING,
  ASYNC_JOB_STATUS_WAITING,
  ASYNC_JOB_STATUS_REJECTED,
  ASYNC_JOB_STATUS_FAILED,
];
const getViewFromLocation = (location) => VIEWS.find((v) => location.hash.includes(v));
const isTranscriptView = (view) => view === 'transcript' || view === 'reader-mode';

function hasVideoAiDataForView(videoAi, view) {
  switch (view) {
  case 'summary':
    return Boolean(videoAi?.videoSummary);
  case 'actions':
    return Boolean(videoAi?.actions?.length);
  case 'chapters':
    return Boolean(videoAi?.chapters?.length);
  case 'ocr':
    return Boolean(videoAi?.ocr?.length);
  case 'keywords':
    return Boolean(videoAi?.keywords?.length);
  default:
    return false;
  }
}

function TranscriptList({
  videoCurrentTime,
  setVideoPlayerCurrentTime,
  transcript,
  assetId,
  activeRevisionID = null,
  videoAi = null,
  keywords = [],
  assetName,
  assetSize,
  assetMimeType,
  manageTranscriptPermission,
  lang = 'en',
  transcribing = 'skipped',
  transcribingErrorCode = null,
  videoAnalyzing = 'skipped',
  isLoaded = false,
  addonsFeatures = {},
}) {
  const defaultView = addonsFeatures?.aiKit?.id ? TRANSCRIPT_VIEW : DEFAULT_VIEW;
  const dispatch = useDispatch();
  const location = useLocation();
  const [copied, setCopied] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [hideInfoBlock, setHideInfoBlock] = useState(Boolean(LocalStorage.get(LOCAL_STORAGE_EXPLORE_KEY)));
  const [searchValue, setSearchValue] = useState('');
  const [view, setView] = useState(getViewFromLocation(location) || LocalStorage.get(LOCAL_STORAGE_VIEW_KEY) || defaultView);
  const isReaderView = view === 'reader-mode';
  const copyTimeoutRef = useRef(null);

  useEffect(() => {
    LocalStorage.set(LOCAL_STORAGE_VIEW_KEY, view);
    window.dispatchEvent(new CustomEvent(CHANGE_VIEW_EVENT, { detail: view }));
  }, [view]);

  useEffect(() => {
    LocalStorage.set(LOCAL_STORAGE_EXPLORE_KEY, hideInfoBlock);
  }, [hideInfoBlock]);

  const handleCopy = useCallback(() => {
    if (view === 'transcript' || view === 'reader-mode') {
      const showDialog = !LocalStorage.get('picsio.dontShowAgainCopyTranscriptDialog');

      Logger.log('User', 'VideoIntelligenceCopyTranscriptClicked', { assetId });
      if (showDialog) {
        CopyTranscriptDialog({ transcript });
        return;
      }
      const isSpeakers = LocalStorage.get('picsio.includeSpeakerToCopyTranscript');
      const isTimestamps = LocalStorage.get('picsio.includeTimestampToCopyTranscript');
      utils.copyTranscript({ transcript, isSpeakers, isTimestamps });
    }

    /** Copy feedback */
    setCopied(true);
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    copyTimeoutRef.current = setTimeout(() => {
      setCopied(false);
    }, COPY_FEEDBACK_TIMEOUT_MS);

    if (view === 'chapters') {
      utils.copyChapters(videoAi?.chapters || []);
      Logger.log('User', 'VideoIntelligenceCopyChaptersClicked', { assetId });
    }
    if (view === 'summary') {
      copyTextToClipboard(videoAi?.videoSummary || '');
      Logger.log('User', 'VideoIntelligenceCopyAISummaryClicked', { assetId });
    }
    if (view === 'actions') {
      utils.copyActions(videoAi?.actions || []);
      Logger.log('User', 'VideoIntelligenceCopyActionsClicked', { assetId });
    }
    if (view === 'ocr') {
      utils.copyOcr(videoAi?.ocr || []);
      Logger.log('User', 'VideoIntelligenceCopyOCRClicked', { assetId });
    }
    if (view === 'keywords') {
      utils.copyKeywords(videoAi?.keywords || []);
      Logger.log('User', 'VideoIntelligenceCopyKeywordsClicked', { assetId });
    }
  }, [view, transcript, assetId, videoAi]);

  const handleDelete = useCallback(async () => {
    Logger.log('User', 'TranscriptDeleteClicked');
    try {
      setLoading(true);
      await sdk.assets.deleteTranscript(assetId, activeRevisionID);
      dispatch(deleteTranscript({ assetId, revisionId: activeRevisionID }));
      Toast(l10n.TRANSCRIPT.TOASTS.textTranscriptionDeleted, { type: 'success' });
    } catch (error) {
      Logger.log('User', 'TranscriptDeleteFailed', { error });
      Toast(l10n.TRANSCRIPT.TOASTS.textTranscriptionNotDeleted, { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [dispatch, assetId, activeRevisionID]);

  const handleClickSuggest = useCallback((initiation_location) => () => {
    Logger.log('User', 'VideoAISuggestImprovementClicked', { tabName: view, initiation_location });
    const { title, text, placeholder } = l10n.TRANSCRIPT.suggestImprovement;
    handleSuggestImprovement(
      title,
      text,
      placeholder,
      { assetId, view, timestamp: videoCurrentTime },
    );
  }, [assetId, view, videoCurrentTime]);

  const showPlaceholder = useMemo(
    () => {
      if (isTranscriptView(view)) {
        return !transcript || PLACEHOLDER_STATUSES.includes(transcribing);
      }
      if (hasVideoAiDataForView(videoAi, view)) return false;
      if (!videoAi) return true;
      return PLACEHOLDER_STATUSES.includes(videoAnalyzing);
    },
    [transcript, transcribing, videoAi, videoAnalyzing, view],
  );

  const isSearchAndCopyAvailable = useMemo(() => {
    switch (view) {
    case 'summary':
      return !!videoAi?.videoSummary;
    case 'transcript':
    case 'reader-mode':
      return !!transcript?.length;
    case 'actions':
      return !!videoAi?.actions?.length;
    case 'chapters':
      return !!videoAi?.chapters?.length;
    case 'ocr':
      return !!videoAi?.ocr?.length;
    case 'keywords':
      return !!videoAi?.keywords?.length;
    default:
      return false;
    }
  }, [view, transcript, videoAi]);

  return (
    <div className={cn('TranscriptList', { disabled: isLoading })}>
      <div className="TranscriptList__search-bar">
        <ViewsSelector value={view} onChange={setView} assetId={assetId} assetName={assetName} />
        <div className="TranscriptList__search-controls">
          <TranscriptSearch setSearchValue={setSearchValue} disabled={!isSearchAndCopyAvailable || showPlaceholder} assetId={assetId} tabName={view} />
          <Tooltip content={l10n.TRANSCRIPT.tooltipCopy(copied, view !== 'reader-mode' && view)} placement="top">
            <IconButton
              onClick={handleCopy}
              size="lg"
              className="fieldCopyToSettings"
              componentProps={{
                'data-testid': 'iconCopyTranscript',
              }}
              disabled={!isSearchAndCopyAvailable || showPlaceholder}
            >
              <Choose>
                <When condition={copied}>
                  <CheckIcon className="checkIcon" />
                </When>
                <Otherwise>
                  <CopyIcon />
                </Otherwise>
              </Choose>
            </IconButton>
          </Tooltip>
          <Choose>
            <When condition={isReaderView || view === 'transcript'}>
              <TranscriptMenu
                isDownloadable={transcript?.length > 0}
                assetId={assetId}
                lang={lang}
                setReaderView={() => {
                  Logger.log('User', `TranscriptReaderMode${isReaderView ? 'Off' : 'On'}`);
                  setView(isReaderView ? 'transcript' : 'reader-mode');
                }}
                onDelete={handleDelete}
                downloadSub={() => {
                  Logger.log('User', 'TranscriptSRTDownloaded');
                  downloadSubFile(transcript, assetName, 'srt');
                }}
                transcript={transcript}
                onSuggestImprovement={handleClickSuggest('tree dot menu')}
              />
            </When>
            <Otherwise>
              <Tooltip content={l10n.TRANSCRIPT.tooltipSuggestImprovement} placement="top">
                <IconButton
                  onClick={handleClickSuggest('line icon')}
                  size="lg"
                  componentProps={{ 'data-testid': 'iconSuggestImprovement' }}
                >
                  <Idea />
                </IconButton>
              </Tooltip>
            </Otherwise>
          </Choose>
        </div>
      </div>
      <If condition={!hideInfoBlock}>
        <PromoBlock onClose={() => setHideInfoBlock(true)} />
      </If>
      <Choose>
        <When condition={showPlaceholder}>
          <TranscriptPlaceholder
            jobStatus={isTranscriptView(view) ? transcribing : videoAnalyzing}
            errorCode={isTranscriptView(view) ? transcribingErrorCode : null}
            assetId={assetId}
            assetSize={assetSize}
            assetMimeType={assetMimeType}
            transcriptPermission={manageTranscriptPermission}
            assetName={assetName}
            activeRevisionID={activeRevisionID}
            view={view === 'reader-mode' ? 'transcript' : view}
            isLoaded={isLoaded}
          />
        </When>
        <Otherwise>
          <ul className="TranscriptList__list">
            <Choose>
              <When condition={view === 'summary'}>
                <AiSummary
                  summary={videoAi?.videoSummary}
                  searchValue={searchValue}
                />
              </When>
              <When condition={view === 'actions'}>
                <Actions
                  actions={videoAi?.actions}
                  searchValue={searchValue}
                  currentTime={videoCurrentTime}
                  setVideoPlayerCurrentTime={setVideoPlayerCurrentTime}
                  revisionId={activeRevisionID}
                  assetId={assetId}
                />
              </When>
              <When condition={view === 'chapters'}>
                <Chapters
                  chapters={videoAi?.chapters}
                  searchValue={searchValue}
                  currentTime={videoCurrentTime}
                  setVideoPlayerCurrentTime={setVideoPlayerCurrentTime}
                  revisionId={activeRevisionID}
                  assetId={assetId}
                />
              </When>
              <When condition={view === 'ocr'}>
                <Ocr
                  ocr={videoAi?.ocr}
                  searchValue={searchValue}
                  currentTime={videoCurrentTime}
                  setVideoPlayerCurrentTime={setVideoPlayerCurrentTime}
                  revisionId={activeRevisionID}
                  assetId={assetId}
                />
              </When>
              <When condition={view === 'keywords'}>
                <Keywords
                  keywords={videoAi?.keywords}
                  assetKeywords={keywords}
                  searchValue={searchValue}
                  setVideoPlayerCurrentTime={setVideoPlayerCurrentTime}
                  revisionId={activeRevisionID}
                  currentTime={videoCurrentTime}
                  assetId={assetId}
                />
              </When>
              <Otherwise>
                <Transcript
                  transcript={transcript}
                  searchValue={searchValue}
                  currentTime={videoCurrentTime}
                  setVideoPlayerCurrentTime={setVideoPlayerCurrentTime}
                  assetId={assetId}
                  revisionId={activeRevisionID}
                  manageTranscriptPermission={manageTranscriptPermission}
                  transcribing={transcribing}
                  isReaderView={isReaderView}
                />
              </Otherwise>
            </Choose>
          </ul>
        </Otherwise>
      </Choose>
    </div>
  );
}

TranscriptList.propTypes = {
  setVideoPlayerCurrentTime: func.isRequired,
  videoCurrentTime: number.isRequired,
  transcript: arrayOf(any).isRequired,
  assetId: string.isRequired,
  activeRevisionID: string,
  assetName: string.isRequired,
  lang: string,
  keywords: arrayOf(any),
  manageTranscriptPermission: bool.isRequired,
  transcribing: oneOf(['skipped', 'waiting', 'running', 'complete', 'failed', 'rejected']),
  transcribingErrorCode: string,
  videoAi: any,
  assetSize: any,
  assetMimeType: string,
  videoAnalyzing: oneOf(['skipped', 'waiting', 'running', 'complete', 'failed', 'rejected']),
  isLoaded: bool,
  addonsFeatures: any,
};

export default TranscriptList;
